import React, {useEffect, useState} from 'react';
import {Box, Button, Container, Link, Paper, Stack, Typography} from '@mui/material';
import api from '../../axios';
import {useLocation, useNavigate} from 'react-router-dom';
import {validateFieldLength} from '../../utility/validationUtil';
import AdmicityForm from '../../shared-components/AdmicityForm';
import {getRouteForUser} from '../../constants/roles';
import useUser from '../../utility/hooks/useUser';
import {getSubdomain} from '../../utility/routeUtil';
import {useGetSchoolInvitesInfoQuery} from '../../api/services/schoolManagementService';
import {useDispatch} from 'react-redux';
import {login, logout} from '../../AppLayout/ApplicationSlice';

const LoginPage = () => {
    const [loginError, setLoginError] = useState('');
    const navigate = useNavigate();
    const {state: locationState} = useLocation();
    const dispatch = useDispatch();
    const {user, account} = useUser();

    const {
        data: invitesInfo = {
            disableStudentInvitations: true,
            disableParentInvitations: true
        },
    } = useGetSchoolInvitesInfoQuery(getSubdomain(),
        {skip: !getSubdomain()}
    );

    useEffect(() => {
        if (user.id && account.isAuthenticated) {
            api.post('/account/session/validate')
                .then(response => {
                    if (response.data) {
                        if (user.domain != null) {
                            const path = locationState?.redirectTo
                                ? locationState?.redirectTo
                                : getRouteForUser(user);
                            window.location.href = `https://${user.domain}${path}`;
                        } else {
                            navigate(getRouteForUser(user), {replace: true});
                        }
                    }
                })
                .catch(reason => {
                    console.error(reason);
                    dispatch(logout());
                });
        }
    }, [user.id, account.isAuthenticated]);

    const handleLogin = async (credentials) => {
        try {
            await dispatch(login(credentials)).unwrap();
        } catch (e) {
            if (e?.message === 'Request failed with status code 401') {
                setLoginError('Incorrect username or password.');
            } else {
                setLoginError('Something went wrong. Please try again later.');
            }
        }
    };

    const textFields = [
        {
            label: 'Email',
            name: 'email',
            type: 'email',
            validate: (value) => validateFieldLength(value, 320)
        },
        {
            label: 'Password',
            type: 'password',
            name: 'password',
        }
    ];

    const getRegionSwitchInfo = () => {
        const host = window.location.hostname;
        const isEU = host.includes('eu.');

        return {
            isEU,
            switchUrl: isEU
                ? 'https://start-with.admicity.co.uk/login'
                : 'https://start-with.eu.admicity.co.uk/login',
            currentRegion: isEU ? 'EU' : 'UK',
            otherRegion: isEU ? 'UK' : 'EU',
        };
    };

    const { switchUrl, currentRegion, otherRegion } = getRegionSwitchInfo();

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100%"
            flexGrow={1}
        >
            <Container maxWidth="xs">
                <Paper
                    variant="outlined"
                    sx={{p: 3, display: 'flex', flexDirection: 'column'}}
                    onClick={() => setLoginError('')}
                >
                    <AdmicityForm
                        title="Log in"
                        textFields={textFields}
                        handleSubmit={handleLogin}
                        handleChange={() => setLoginError('')}
                        buttonText="Log In"
                        errorCaption={loginError}
                    />
                    <Stack display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                        <Button variant={'text'}
                                sx={{textTransform: 'none'}}
                                onClick={() => {
                                    navigate('/password-recovery');
                                }}>
                            Forgot password?
                        </Button>
                        {(!invitesInfo.disableParentInvitations || !invitesInfo.disableStudentInvitations || !invitesInfo.disableApplicationParentInvitations) &&
                            <Button variant={'text'}
                                    sx={{textTransform: 'none'}}
                                    onClick={() => {
                                        navigate('/invite-recovery');
                                    }}>
                                Resend registration link
                            </Button>}
                    </Stack>
                    <Box mt={1} textAlign="center">
                        <Typography variant="body2">
                            {currentRegion} | <Link href={switchUrl}>{otherRegion}</Link>
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default LoginPage;
